// DocumentViewer — renders a PDF or image inline, inside the app.
//
// PDFs are rendered to <canvas> via pdf.js (NOT an <iframe>/<embed>), because
// HelpDesk Pro runs framed inside Microsoft Teams and nested frame embedding of
// a blob URL is unreliable there. Canvas rendering fetches the bytes over the
// SAS URL and paints them, which works the same framed or standalone.
//
// The pdf.js worker is bundled locally (worker-entry pattern) so there is no
// CDN dependency — important both for Teams CSP and for offline reliability.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
// eslint-disable-next-line import/no-webpack-loader-syntax
import PdfWorker from 'pdfjs-dist/build/pdf.worker.entry';

// Point pdf.js at the locally-bundled worker.
(pdfjsLib as any).GlobalWorkerOptions.workerSrc = PdfWorker;

const C = {
  primary: 'var(--c-primary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  danger: 'var(--c-danger)',
};

type ViewerKind = 'pdf' | 'image';

export interface DocumentViewerProps {
  /** Function that returns a fresh, short-lived SAS URL for the file. */
  getUrl: () => Promise<string>;
  kind: ViewerKind;
  fileName?: string | null;
  /** Max render width in px (defaults to container width). */
  maxWidth?: number;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  getUrl,
  kind,
  fileName,
  maxWidth,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errMsg, setErrMsg] = useState('');
  const [imgUrl, setImgUrl] = useState<string>('');
  const [numPages, setNumPages] = useState(0);
  const renderTokenRef = useRef(0);

  const renderPdf = useCallback(async (url: string, token: number) => {
    const loadingTask = (pdfjsLib as any).getDocument({ url });
    const pdf = await loadingTask.promise;
    if (token !== renderTokenRef.current) return; // a newer render started
    setNumPages(pdf.numPages);

    const host = containerRef.current;
    if (!host) return;
    host.innerHTML = '';

    const targetWidth =
      maxWidth ?? Math.min(host.clientWidth || 800, 1000);

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      if (token !== renderTokenRef.current) return;
      const page = await pdf.getPage(pageNum);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = targetWidth / baseViewport.width;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      // Account for device pixel ratio so text stays crisp.
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      canvas.style.display = 'block';
      canvas.style.margin = '0 auto 12px';
      canvas.style.boxShadow = '0 1px 4px rgba(0,0,0,0.15)';
      canvas.style.borderRadius = '2px';
      ctx.scale(dpr, dpr);

      host.appendChild(canvas);
      await page.render({ canvasContext: ctx, viewport }).promise;
    }
  }, [maxWidth]);

  useEffect(() => {
    let cancelled = false;
    const token = ++renderTokenRef.current;
    setStatus('loading');
    setErrMsg('');
    (async () => {
      try {
        const url = await getUrl();
        if (cancelled || token !== renderTokenRef.current) return;
        if (kind === 'image') {
          setImgUrl(url);
          setStatus('ready');
        } else {
          await renderPdf(url, token);
          if (!cancelled && token === renderTokenRef.current) setStatus('ready');
        }
      } catch (e: any) {
        if (cancelled) return;
        console.error('[DocumentViewer]', e);
        setErrMsg(e?.message ?? 'Could not load document');
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [getUrl, kind, renderPdf]);

  return (
    <div style={{ background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', borderBottom: `1px solid ${C.border}`,
        background: C.surface, fontSize: 13, color: C.textMuted,
      }}>
        <span style={{ fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {fileName || (kind === 'pdf' ? 'Document' : 'Image')}
        </span>
        <span>
          {status === 'ready' && kind === 'pdf' && numPages > 0 && `${numPages} page${numPages > 1 ? 's' : ''} · `}
          <button
            onClick={async () => {
              try { window.open(await getUrl(), '_blank', 'noopener'); }
              catch { /* ignore */ }
            }}
            style={{
              background: 'none', border: 'none', color: C.primary,
              cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline',
            }}
          >Open in new tab</button>
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: 16, maxHeight: '75vh', overflowY: 'auto' }}>
        {status === 'loading' && (
          <div style={{ padding: 40, textAlign: 'center', color: C.textMuted }}>
            Loading {kind === 'pdf' ? 'document' : 'image'}…
          </div>
        )}
        {status === 'error' && (
          <div style={{ padding: 24, color: C.danger, fontSize: 14 }}>
            Couldn't display this {kind === 'pdf' ? 'document' : 'image'} inline.
            {errMsg ? <div style={{ marginTop: 6, fontSize: 12, color: C.textMuted }}>{errMsg}</div> : null}
            <div style={{ marginTop: 12 }}>
              <button
                onClick={async () => { try { window.open(await getUrl(), '_blank', 'noopener'); } catch {} }}
                style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}
              >Open in new tab instead</button>
            </div>
          </div>
        )}

        {/* PDF canvases mount here */}
        <div ref={containerRef} style={{ display: kind === 'pdf' && status === 'ready' ? 'block' : 'none' }} />

        {/* Image */}
        {kind === 'image' && status === 'ready' && imgUrl && (
          <img
            src={imgUrl}
            alt={fileName || 'attachment'}
            style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto', borderRadius: 4 }}
          />
        )}
      </div>
    </div>
  );
};
